import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { codexApi } from '../api';
import { celebrate } from '../../lib/celebrate';
import { Feather, Trash2, Edit3, Send, Eye, EyeOff, X, Plus, Sparkles } from 'lucide-react';

const MOODS = [
  { id: 'awe',         label: 'Awe',         emoji: '🌌' },
  { id: 'grateful',    label: 'Grateful',    emoji: '🙏' },
  { id: 'convicted',   label: 'Convicted',   emoji: '⚡' },
  { id: 'peaceful',    label: 'Peaceful',    emoji: '🕊' },
  { id: 'stirred',     label: 'Stirred',     emoji: '🔥' },
  { id: 'questioning', label: 'Questioning', emoji: '❓' },
  { id: 'joyful',      label: 'Joyful',      emoji: '✨' },
];

export default function CodexJournal() {
  const location = useLocation();
  const navigate = useNavigate();
  const seed = location.state || {};
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(seed?.prompt ? {
    open: true,
    word_id: seed.word_id || null,
    word_transliteration: seed.word_transliteration || null,
    prompt: seed.prompt,
    content: '',
    mood: null,
    share_with_mentor: false,
  } : null);
  const [editing, setEditing] = useState(null);

  // Clear the location state so re-visits don't re-trigger the composer.
  useEffect(() => {
    if (seed?.prompt) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await codexApi.journalList();
      setItems(res.items);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const newEntry = () => setComposer({
    open: true,
    word_id: null,
    word_transliteration: null,
    prompt: null,
    content: '',
    mood: null,
    share_with_mentor: false,
  });

  const save = async () => {
    if (!composer?.content.trim()) return;
    await codexApi.journalCreate({
      word_id: composer.word_id,
      prompt: composer.prompt,
      content: composer.content,
      mood: composer.mood,
      share_with_mentor: composer.share_with_mentor,
    });
    celebrate(0.55);
    setComposer(null);
    load();
  };

  const persistEdit = async () => {
    if (!editing?.content.trim()) return;
    await codexApi.journalUpdate(editing.id, {
      content: editing.content,
      mood: editing.mood,
      share_with_mentor: editing.share_with_mentor,
    });
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this reflection? This cannot be undone.')) return;
    await codexApi.journalDelete(id);
    load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', opacity: 0.7 }}>Opening your journal…</div>;

  return (
    <div data-testid="codex-journal" style={{ paddingTop: 8 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ marginBottom: 18, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div className="codex-eyebrow">YOUR PRIVATE JOURNAL</div>
          <h1 className="codex-h1" style={{ marginTop: 6 }}>Sacred Reflection</h1>
          <p style={{ opacity: 0.75, marginTop: 6, maxWidth: 540 }}>
            What is the Spirit saying to you? Write boldly. You choose what (if anything) to share with your mentor.
          </p>
        </div>
        <button onClick={newEntry} className="codex-btn codex-btn-primary" data-testid="codex-journal-new">
          <Plus size={16} /> New Reflection
        </button>
      </motion.div>

      {/* Composer */}
      <AnimatePresence>
        {composer?.open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            className="codex-card-gold codex-card"
            style={{ padding: 22, marginBottom: 18 }}
            data-testid="codex-journal-composer"
          >
            {composer.prompt && (
              <div className="codex-card" style={{ padding: 12, marginBottom: 14, background: 'rgba(0,0,0,0.2)' }}>
                <div className="codex-eyebrow">PROMPT {composer.word_transliteration ? `· ${composer.word_transliteration}` : ''}</div>
                <p style={{ marginTop: 6, fontSize: 14, opacity: 0.9 }}>{composer.prompt}</p>
              </div>
            )}
            <textarea
              autoFocus
              rows={6}
              value={composer.content}
              onChange={(e) => setComposer({ ...composer, content: e.target.value })}
              placeholder="Pour out your heart…"
              className="codex-input"
              style={{ fontSize: 15, lineHeight: 1.7, fontFamily: 'inherit', resize: 'vertical' }}
              data-testid="codex-journal-content"
            />

            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {MOODS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setComposer({ ...composer, mood: composer.mood === m.id ? null : m.id })}
                  className="codex-card"
                  style={{
                    padding: '6px 10px',
                    fontSize: 12,
                    cursor: 'pointer',
                    color: 'var(--codex-parchment)',
                    borderColor: composer.mood === m.id ? 'rgba(212,175,55,0.7)' : undefined,
                    background: composer.mood === m.id ? 'rgba(212,175,55,0.15)' : undefined,
                  }}
                >
                  {m.emoji} {m.label}
                </button>
              ))}
            </div>

            <label
              style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, opacity: 0.85, cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={composer.share_with_mentor}
                onChange={(e) => setComposer({ ...composer, share_with_mentor: e.target.checked })}
                data-testid="codex-journal-share-toggle"
              />
              {composer.share_with_mentor
                ? <span><Eye size={14} style={{ verticalAlign: -2, color: 'var(--codex-gold-light)' }} /> Share with my mentor</span>
                : <span><EyeOff size={14} style={{ verticalAlign: -2 }} /> Keep this private</span>}
            </label>

            <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
              <button onClick={() => setComposer(null)} className="codex-btn codex-btn-ghost">
                <X size={14} /> Cancel
              </button>
              <button
                onClick={save}
                disabled={!composer.content.trim()}
                className="codex-btn codex-btn-primary"
                style={{ opacity: composer.content.trim() ? 1 : 0.4 }}
                data-testid="codex-journal-save"
              >
                <Send size={14} /> Seal Reflection
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List */}
      {items.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="codex-card"
          style={{ padding: 60, textAlign: 'center' }}
        >
          <motion.div animate={{ rotate: [0, -8, 8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }} style={{ display: 'inline-block' }}>
            <Feather size={48} color="var(--codex-gold-light)" />
          </motion.div>
          <div className="codex-display" style={{ marginTop: 18, fontSize: 22 }}>The blank scroll calls to you.</div>
          <div style={{ opacity: 0.75, marginTop: 6, fontSize: 14 }}>
            Write your first reflection — even a single sentence is a holy act.
          </div>
          <button onClick={newEntry} className="codex-btn codex-btn-primary" style={{ marginTop: 18 }}>
            <Sparkles size={14} /> Begin Writing
          </button>
        </motion.div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {items.map((it, i) => {
            const m = MOODS.find((mm) => mm.id === it.mood);
            return (
              <motion.div
                key={it.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.3) }}
                className="codex-card"
                style={{ padding: 18 }}
                data-testid={`codex-journal-item-${it.id}`}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span className="codex-eyebrow">
                    {new Date(it.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' })}
                  </span>
                  {it.word_transliteration && (
                    <span className="codex-display" style={{ fontSize: 13, color: 'var(--codex-gold-light)' }}>· {it.word_transliteration}</span>
                  )}
                  {m && <span style={{ fontSize: 12, opacity: 0.75 }}>· {m.emoji} {m.label}</span>}
                  {it.share_with_mentor ? (
                    <span style={{ fontSize: 11, color: '#7ee2a3', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Eye size={12} /> Shared with mentor
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, opacity: 0.5, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <EyeOff size={12} /> Private
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setEditing({ ...it })}
                      className="codex-btn codex-btn-ghost"
                      style={{ padding: '5px 8px', fontSize: 11 }}
                      data-testid={`codex-journal-edit-${it.id}`}
                    ><Edit3 size={12} /></button>
                    <button
                      onClick={() => remove(it.id)}
                      className="codex-btn codex-btn-ghost"
                      style={{ padding: '5px 8px', fontSize: 11, borderColor: 'rgba(239,68,68,0.3)', color: '#f87171' }}
                      data-testid={`codex-journal-delete-${it.id}`}
                    ><Trash2 size={12} /></button>
                  </span>
                </div>
                {it.prompt && (
                  <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 8, fontStyle: 'italic' }}>
                    "{it.prompt}"
                  </div>
                )}
                <p style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{it.content}</p>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Edit modal */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditing(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(11,10,22,0.85)', backdropFilter: 'blur(8px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="codex-card-gold codex-card"
              style={{ padding: 24, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
              data-testid="codex-journal-edit-modal"
            >
              <div className="codex-eyebrow">EDIT REFLECTION</div>
              <textarea
                rows={6}
                value={editing.content}
                onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                className="codex-input"
                style={{ marginTop: 10, fontSize: 15, lineHeight: 1.7, fontFamily: 'inherit', resize: 'vertical' }}
              />
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {MOODS.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setEditing({ ...editing, mood: editing.mood === m.id ? null : m.id })}
                    className="codex-card"
                    style={{
                      padding: '6px 10px',
                      fontSize: 12,
                      cursor: 'pointer',
                      color: 'var(--codex-parchment)',
                      borderColor: editing.mood === m.id ? 'rgba(212,175,55,0.7)' : undefined,
                      background: editing.mood === m.id ? 'rgba(212,175,55,0.15)' : undefined,
                    }}
                  >
                    {m.emoji} {m.label}
                  </button>
                ))}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={editing.share_with_mentor}
                  onChange={(e) => setEditing({ ...editing, share_with_mentor: e.target.checked })}
                />
                {editing.share_with_mentor
                  ? <span><Eye size={14} style={{ verticalAlign: -2, color: 'var(--codex-gold-light)' }} /> Share with my mentor</span>
                  : <span><EyeOff size={14} style={{ verticalAlign: -2 }} /> Keep this private</span>}
              </label>
              <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
                <button onClick={() => setEditing(null)} className="codex-btn codex-btn-ghost">Cancel</button>
                <button onClick={persistEdit} className="codex-btn codex-btn-primary">Save</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
